/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useContext, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getSocket } from '../../utils/Socket';
import { useNotifications } from '../notifications/NotificationContext';
import { useBanner } from '../banners/BannerContext';
// IMPORTAÇÃO DE CONTEXTO: Usamos o Context para obter as variáveis de Auth
import { Context as AuthCtx } from '../authentication/AuthContext';

// IMPORTAÇÕES NECESSÁRIAS
import { useGlobalAudio } from '../audio/GlobalAudioContext';
import { getOrdersResumo, updateOrderStatus, getDataMenu } from '../../components/helpers/Api';

export default function SocketListener() {
  const location = useLocation();
  const { add } = useNotifications();
  const { showBanner } = useBanner();

  // OBTENDO DADOS DO SEU AuthContext
  const authContext = useContext(AuthCtx);
  const isAuthenticated = authContext?.isAuthenticated;
  const forceLogout = authContext?.forceLogout;

  const { playSound, playAlertSound } = useGlobalAudio();

  const intervalRef = useRef(null);
  const menuConfigIntervalRef = useRef(null);
  // Cache de aceita_automatico — raramente muda, não precisa vir a cada 10s
  const menuConfigRef = useRef({ aceitaAutomatico: false });
  // Evita chamadas concorrentes de auto-aceite para o mesmo pedido
  const pedidosEmAceiteRef = useRef(new Set());

  // --- normaliza nível do banner ---
  function getLevelFromMsg(msg) {
    const raw = (msg?.level || msg?.tipo || msg?.severity || '').toString().toLowerCase();
    if (['critical', 'bloqueio', 'block'].includes(raw)) return 'critical';
    if (['error', 'erro', 'danger'].includes(raw)) return 'error';
    if (['warning', 'warn', 'aviso'].includes(raw)) return 'warning';
    if (['success', 'ok', 'sucesso'].includes(raw)) return 'success';
    if (['info', 'informativo', 'informativa'].includes(raw)) return 'info';
    return 'info';
  }

  // --- decide quando exibir banner ---
  function shouldShowBanner(msg) {
    if (msg?.banner === true) return true;
    const level = getLevelFromMsg(msg);
    if (['warning', 'error', 'critical'].includes(level)) return true;
    if (msg?.actions?.block === true) return true;
    return false;
  }

  // --- título padrão por nível ---
  function defaultTitleFromLevel(level) {
    switch (level) {
      case 'critical':
        return 'Ação necessária';
      case 'error':
        return 'Ocorreu um erro';
      case 'warning':
        return 'Atenção';
      case 'success':
        return 'Sucesso';
      default:
        return 'Informação';
    }
  }

  // Atualiza cache de aceita_automatico (chamado uma vez ao montar e a cada 2 minutos)
  const fetchMenuConfig = async () => {
    try {
      const resMenu = await getDataMenu();
      const valorAceite = resMenu?.data?.data?.aceita_automatico;
      menuConfigRef.current = {
        aceitaAutomatico: valorAceite === true || valorAceite === 1 || valorAceite === 'true',
      };
    } catch {
      // mantém valor anterior em caso de falha
    }
  };

  // Safety net de 30s: socket é o mecanismo primário (new_order event)
  const checkNewOrders = async () => {
    try {
      const resPedidos = await getOrdersResumo();
      const aceitaAgora = menuConfigRef.current.aceitaAutomatico;

      const pedidos = resPedidos.data.data.rows.filter((pedido) => pedido.mesa?.mesadelivery === 'D');

      for (const elemento of pedidos) {
        if (elemento.situacao === 'analisando') {
          playSound();

          // TOTEM fica de fora do auto-aceite: só existe em "analisando"
          // quando é pagamento em dinheiro pendente no caixa — auto-aceitar
          // liberaria a produção sem ninguém ter confirmado o pagamento
          // (mesmo motivo do autoAceiteWorker.js). O som acima continua
          // tocando normalmente, só pra alertar a equipe.
          if (aceitaAgora && elemento.origem !== 'TOTEM') {
            const codPedido = elemento.cod_pedidomesa;
            if (pedidosEmAceiteRef.current.has(codPedido)) {
              continue;
            }
            pedidosEmAceiteRef.current.add(codPedido);

            const data = {
              situacao: 'produzindo',
              imprimiu: 'N',
              impresso: 'N'
            };
            try {
              await updateOrderStatus(codPedido, data);
              const s = getSocket();
              s.emit('update_status', {
                consumer: elemento.telefone_cliente || 'auto',
                cod_org: sessionStorage.getItem('cod_org'),
                cod_pedidomesa: codPedido,
              });
              window.dispatchEvent(new CustomEvent('atualizarPedidos'));
            } catch (error) {
              console.error('Erro ao atualizar status automatico:', error);
            } finally {
              pedidosEmAceiteRef.current.delete(codPedido);
            }
          }
        }
      }
    } catch (error) {
      console.error('Erro ao buscar pedidos no listener global:', error);
    }
  };

  // Efeito para iniciar o intervalo global de 10 segundos (MODIFICADO E CORRIGIDO)
  useEffect(() => {
    const currentPath = location.pathname.toLowerCase();

    // O intervalo de novos pedidos deve rodar SOMENTE em rotas administrativas
    const allowedPaths = ['/app', '/painel', '/admin', '/pedidos'];
    const isAllowed = allowedPaths.some((path) => currentPath.includes(path));

    // Condição para iniciar o intervalo: DEVE estar autenticado E estar em rota administrativa
    if (isAuthenticated && isAllowed) {
      // Socket é o mecanismo primário; polling a 30s é só safety net em caso de falha de socket
      const intervalTime = 30000;

      // Carrega aceita_automatico uma vez ao montar e renova a cada 2 minutos
      fetchMenuConfig();
      menuConfigIntervalRef.current = setInterval(fetchMenuConfig, 120000);

      // Quando o usuário altera o toggle em outra tela do mesmo domínio,
      // refaz a leitura imediatamente para que o auto-aceite reflita o valor
      // atual sem precisar de F5.
      const handleRefreshAceite = () => fetchMenuConfig();
      window.addEventListener('refreshAceiteAutomatico', handleRefreshAceite);

      const codOrg = sessionStorage.getItem('cod_org');
      const s = getSocket();
      const handleNewOrder = () => checkNewOrders();
      if (codOrg) {
        s.on(`new_order${codOrg}`, handleNewOrder);
      }

      const resetInterval = () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }

        checkNewOrders();
        intervalRef.current = setInterval(checkNewOrders, intervalTime);
      };

      resetInterval();

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        if (menuConfigIntervalRef.current) {
          clearInterval(menuConfigIntervalRef.current);
        }
        if (codOrg) {
          s.off(`new_order${codOrg}`, handleNewOrder);
        }
        window.removeEventListener('refreshAceiteAutomatico', handleRefreshAceite);
      };
    } else {
      // Se a condição não for atendida (deslogado ou em rota proibida), garante que o intervalo está limpo
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [isAuthenticated, location.pathname, playSound]);

  // Alerta de agendamento pendente de confirmação — igual ao bloco acima, só
  // roda em rotas administrativas. Ao chegar um pedido `agendado` ainda não
  // confirmado, avisa com um toast (não abre nada sozinho — o operador
  // confirma quando quiser, abrindo o pedido normalmente na aba Agendados).
  useEffect(() => {
    const currentPath = location.pathname.toLowerCase();
    const allowedPaths = ['/app', '/painel', '/admin', '/pedidos'];
    const isAllowed = allowedPaths.some((path) => currentPath.includes(path));
    if (!isAuthenticated || !isAllowed) return;

    const codOrg = sessionStorage.getItem('cod_org');
    if (!codOrg) return;

    const s = getSocket();
    const handleNewAgendado = (payload) => {
      if (payload?.order?.situacao !== 'agendado') return;
      if (payload.order.agendamento_confirmado) return;
      playSound();
      toast.info('🕓 Novo pedido agendado pendente de confirmação! Veja em "Agendados".');
    };

    s.on(`new_order${codOrg}`, handleNewAgendado);
    return () => s.off(`new_order${codOrg}`, handleNewAgendado);
  }, [isAuthenticated, location.pathname]);

  // Notificação de novo Pedido (tb_venda tipo='P') pro PDV — separada do
  // bloco acima porque aquele só roda em rotas administrativas
  // (allowedPaths), e o PDV vive em /pdv. Repassa o payload bruto (que traz
  // `tipo: 'PEDIDO' | 'PRE_VENDA'`, já que o mesmo evento do varejoController
  // é emitido nos dois casos) — quem decide se toca som/toast é Pdv.jsx,
  // filtrando por tipo==='PEDIDO' (gerar_venda_loja='S'); PRE_VENDA não
  // aparece no PDV, então não deve notificar lá.
  useEffect(() => {
    if (!isAuthenticated) return;
    const codOrg = sessionStorage.getItem('cod_org');
    if (!codOrg) return;

    const s = getSocket();
    const handleNewPdvOrder = (data) => {
      window.dispatchEvent(new CustomEvent('novoPedidoPdv', { detail: data }));
    };
    const handleCancelPdvOrder = (data) => {
      window.dispatchEvent(new CustomEvent('cancelarPedidoPdv', { detail: data }));
    };
    s.on(`new_pdv_order${codOrg}`, handleNewPdvOrder);
    s.on(`cancel_pdv_order${codOrg}`, handleCancelPdvOrder);

    return () => {
      s.off(`new_pdv_order${codOrg}`, handleNewPdvOrder);
      s.off(`cancel_pdv_order${codOrg}`, handleCancelPdvOrder);
    };
  }, [isAuthenticated]);

  // Lógica de escuta de socket (mensagem_recebida, etc.)
  useEffect(() => {
    const s = getSocket();

    const handle = (msg) => {
      // não exibir nada no painel/admin
      const path = location.pathname.toLowerCase();
      if (path.includes('/painel') || path.includes('/admin') || path.startsWith('/app')) return;

      add(msg);

      // Se o título ou o remetente indicar que é do WhatsApp, toca o som de alerta
      if (msg.titulo?.includes('WhatsApp') || msg.remetente === 'Bot WhatsApp') {
        playAlertSound();
      }

      // 2) banner (quando aplicável)
      if (shouldShowBanner(msg)) {
        const level = getLevelFromMsg(msg);

        const sticky =
          msg?.sticky === true || msg?.actions?.sticky === true || level === 'critical';

        const durationMs = Number.isFinite(Number(msg?.durationMs))
          ? Number(msg.durationMs)
          : undefined;

        const expiresAt = msg?.expiresAt;
        const daysLeft = Number.isFinite(Number(msg?.daysLeft)) ? Number(msg.daysLeft) : undefined;

        showBanner({
          title: msg?.titulo || defaultTitleFromLevel(level),
          message: msg?.texto || '',
          level,
          sticky,
          durationMs,
          expiresAt,
          daysLeft,
        });
      }

      // 3) bloqueio / logout forçado durante a sessão
      // Só as ações explícitas do modal (block/logout) forçam logout — a severidade
      // "bloqueio" é só a cor/rótulo visual da mensagem, não deve por si só derrubar a sessão.
      const isBlock = msg?.actions?.block === true;
      const mustLogout = msg?.actions?.logout === true;

      const situacaoEmpresa = (msg?.situacao || msg?.situacao_empresa || '')
        .toString()
        .toUpperCase();
      const empresaInativa = ['INATIVO', 'SUSPENSO', 'BLOQUEADO'].includes(situacaoEmpresa);

      if (isBlock || mustLogout || empresaInativa) {
        const motivo =
          msg?.texto ||
          'Sessão encerrada: sua empresa foi bloqueada. Entre em contato com o suporte.';
        // Chama o forceLogout do AuthContext (correto)
        forceLogout?.(motivo);
      }
    };

    // Handler para canal direto de status da empresa (admin/worker)
    const handleEmpresaStatus = (payload) => {
      const situacao = String(payload?.situacao || '').toUpperCase();
      if (situacao !== 'ATIVO') {
        const motivo =
          payload?.motivo ||
          'Sua empresa foi suspensa. Entre em contato com o suporte.';
        forceLogout?.(motivo);
      }
    };

    // canais
    s.on('mensagem_recebida', handle);

    const cod_org = sessionStorage.getItem('cod_org');
    const cod_empresa = sessionStorage.getItem('cod_empresa');
    const cod_usuario = sessionStorage.getItem('cod_usuario');

    if (cod_org) s.on(`mensagem_recebida_org_${cod_org}`, handle);
    if (cod_org && cod_empresa) s.on(`mensagem_recebida_emp_${cod_org}_${cod_empresa}`, handle);
    if (cod_usuario) s.on(`mensagem_recebida_user_${cod_usuario}`, handle);
    if (cod_org && cod_empresa) s.on(`empresa_status_${cod_org}_${cod_empresa}`, handleEmpresaStatus);

    return () => {
      s.off('mensagem_recebida', handle);
      if (cod_org) s.off(`mensagem_recebida_org_${cod_org}`, handle);
      if (cod_org && cod_empresa) s.off(`mensagem_recebida_emp_${cod_org}_${cod_empresa}`, handle);
      if (cod_usuario) s.off(`mensagem_recebida_user_${cod_usuario}`, handle);
      if (cod_org && cod_empresa) s.off(`empresa_status_${cod_org}_${cod_empresa}`, handleEmpresaStatus);
    };
  }, [location.pathname, add, showBanner, forceLogout]);

  return null;
}
